import {AfterViewInit, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {AbstractControl} from '@angular/forms';
import {Metrics} from './metrics';
import {CustomMetricsModel} from '../models/metrics/custom-metrics.model';
import {FieldType} from '../../models/field-type';
import {PredefinedValuesService} from '../../../core/services/impl/section-services/predefined-values/predefined-values.service';
import {isCarOne, isKIOSKPOLIS} from '../../../core/utils/utils';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {TextTransformService} from '../../services/text-transform.service';
import {FormFieldService} from '../../services/form-field.service';
import {FormControlStatus} from '../../enums/form-control-status.enum';
import * as _ from 'lodash';
import {FloatLabelType, MatFormFieldAppearance} from '@angular/material';

export class AbstractFieldComponent extends Metrics implements OnInit, AfterViewInit {

  @Input() bean: any;
  @Input() isDirtyBean: boolean = false;
  @Input() context: any;
  @Input() fieldConfig: any;
  @Input() control: AbstractControl;
  @Input() errorsMap: Map<string, string[]>;
  @Input() customMetrics: CustomMetricsModel;

  @Output() changeEvent = new EventEmitter();
  @Output() markAsDirty = new EventEmitter();

  /**
   * should emit on click on interactive part of field
   * e.g. should emit when user clicks on radio tile's option,
   * shouldn't emit when user clicks on radio tile's label
   */
  @Output() interactiveClick: EventEmitter<InteractiveFieldClickEvent> = new EventEmitter<InteractiveFieldClickEvent>();

  appearance: MatFormFieldAppearance;
  placeholder: string;
  label: string;
  floatLabel: FloatLabelType = 'auto';
  private isDropdownTypeField: boolean;
  private errorsHistory: string[] = [];
  private readonly GA4_CLICK_EVENT_WITH_VALUE_FIELD_TYPES: FieldType[] = [
      FieldType.Radio,
      FieldType.Checkbox,
      FieldType.ButtonToggle,
      FieldType.RadioCheckboxTile,
  ];

  constructor(public formFieldService: FormFieldService) {
    super(formFieldService.metricsService);
  }

  // commented line is definition of original requirements for styling input for predefined value
  get predefinedStyleEnabled(): boolean {
    return false;
    // return this.isPredefinedValue;
  }

  get isPredefinedValue() {
    if (this.isDirtyBean) {
      return false;
    }
    if (isKIOSKPOLIS() && !this.formFieldService.isDataFromAztec) { // display predefined value message for KIOSKPOLIS only when data was retrieved from aztec
      return false;
    }
    return this.fieldConfig && this.fieldConfig.hasOwnProperty(PredefinedValuesService.PREDEFINED) && this.fieldConfig.predefined.isPredefined && this.isPristineAndFulfilled();
  }

  get tooltipIconVisible(): boolean {
    return isNotNullOrUndefined(this.fieldConfig.description);
  }

  get informationIconVisible(): boolean {
    if (isCarOne()) {
      return false;
    }
    return this.isPredefinedValue;
  }

  get shouldPassValueToClickEvent(): boolean {
    return this.GA4_CLICK_EVENT_WITH_VALUE_FIELD_TYPES.includes(this.fieldConfig.type);
  }

  get hasCaption(): boolean {
    return !!this.fieldConfig.caption;
  }

  get autocompleteAllowed(): boolean {
    return this.formFieldService.autocompleteAllowed;
  }

  get isAppearanceOutline(): boolean {
    return this.appearance === 'outline';
  }

  // get predefinedInfo(): string {
  //   return this.fieldConfig.predefined.additionalInfo[this.translateService.defaultLang];
  // }

  ngOnInit(): void {
    if (isNotNullOrUndefined(this.fieldConfig.initialValue) && (!isNotNullOrUndefined(this.control.value) || this.control.value === '')) {
      this.control.setValue(this.fieldConfig.initialValue);
    }
  }

  ngAfterViewInit() {
    if (this.fieldConfig.type === FieldType.Select || this.fieldConfig.type === FieldType.DateField) {
      this.isDropdownTypeField = true;
    } else {
      this.isDropdownTypeField = false;
    }
  }

  async valueLabel() {
    return this.control.value;
  }

  onChange(event) {
    this.handlePredefined();
    this.changeEvent.emit({ key: this.fieldConfig.fieldKey, value: this.control.value });

    if (!this.isDropdownTypeField) {
      this.initMetrics(this.fieldConfig, this.customMetrics);
    }

    const value = this.fieldConfig.personalData ? this.maskPersonalData(this.control.value) : this.control.value;

    this.changeMetric(value);

    if (!this.isDropdownTypeField) {
      this.sendMetric();
    }
  }

  onOpen(): void {
    if (this.isDropdownTypeField) {
      this.initMetrics(this.fieldConfig, this.customMetrics);
    }
    this.openMetric();
  }

  async onClick(event?: any) {
    const value = this.shouldPassValueToClickEvent ? await this.valueLabel() : null;
    this.formFieldService.onClick(this.fieldConfig, value);
    this.emitInteractiveClick();
  }

  emitInteractiveClick() {
    this.interactiveClick.emit({
      fieldLabel: this.formFieldService.fieldCaptionTranslated(this.fieldConfig)
    });
  }

  onClose(): void {
    this.closeMetric();
    this.sendMetric();
    this.handlePredefined();
  }

  public handleStatusChanges(): void {
    this.control.statusChanges.subscribe((status: FormControlStatus) => {
      if (status === FormControlStatus.INVALID) {
        if (isNotNullOrUndefined(this.control.errors)) {
          const errors = Object.keys(this.control.errors)
              .map(error => {
                    if (this.control.errors[error] && _.isString(this.control.errors[error])) {
                      return this.control.errors[error];
                    }
                    return null;
                  }
              )
              .filter(errorTranslationKey => isNotNullOrUndefined(errorTranslationKey));
          if (errors && !!errors.length) {
            const newErrors = errors.filter(error => !this.errorsHistory.includes(error));
            this.formFieldService.onError(this.fieldConfig, newErrors);
            this.errorsHistory.push(...newErrors);
          }
        }
      }
    });
  }

  initFieldAppearance() {
    this.initAppearance();
    this.initFloatLabel();
  }

  initAppearance() {
    this.appearance = this.fieldConfig.appearance ? this.fieldConfig.appearance : 'legacy';
  }

  initFloatLabel(): void {
    // if (isCarOne()) {
    //   this.floatLabel = 'always';
    // }
  }

  private handlePredefined() {
    const key = `${this.context}.${this.fieldConfig.fieldKey}`;
    this.control.markAsDirty();
    this.markAsDirty.emit({ [key]: true });
  }

  private isPristineAndFulfilled(): boolean {
    return this.control.pristine && this.control.value != null && !!(this.control.value + '');
  }

  private maskPersonalData(value: any): string {
    return TextTransformService.maskValue(value, '*', 3);
  }

  get id(): string {
    let id = '';
    if (this.customMetrics) {
      id += this.customMetrics.sectionKey + '-' + this.customMetrics.count + '-';
    }
    const suffix = this.fieldConfig.maskConfig ? 'MaskInput' : 'Input';
    id += this.fieldConfig.fieldKey + suffix;
    return id;
  }
}

export interface InteractiveFieldClickEvent {
  sectionName?: string;
  fieldLabel: string;
}
